import { useEffect, useState } from "react";
import { ReceiptIndianRupee, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { rupee } from "@/lib/format";
import { Badge } from "@/components/ui/badge";

export default function SalesLog() {
  const [data, setData] = useState({ audits: [], total_value: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/stock-audits").then(({ data }) => setData(data)).finally(() => setLoading(false)); }, []);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-extrabold flex items-center gap-2"><ReceiptIndianRupee className="h-6 w-6 text-primary" /> Sales Log</h1>
        <p className="text-muted-foreground text-sm font-medium">Every inventory reduction logged as a partial sale — audit trail of stock cleared by sellers.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-card border rounded-xl p-4">
          <TrendingUp className="h-5 w-5 text-primary" />
          <div className="mt-2 font-heading text-2xl font-extrabold">{rupee(data.total_value)}</div>
          <div className="text-xs text-muted-foreground font-semibold">Total Sales Value Logged</div>
        </div>
        <div className="bg-card border rounded-xl p-4">
          <ReceiptIndianRupee className="h-5 w-5 text-primary" />
          <div className="mt-2 font-heading text-2xl font-extrabold">{data.count}</div>
          <div className="text-xs text-muted-foreground font-semibold">Reduction Events</div>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-16">Loading…</div>
      ) : data.audits.length === 0 ? (
        <div className="text-center text-muted-foreground py-16 font-semibold">No inventory reductions yet.</div>
      ) : (
        <div className="bg-card border rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="sales-log-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                <th className="p-3 font-bold">Date</th>
                <th className="p-3 font-bold">Product</th>
                <th className="p-3 font-bold">Seller</th>
                <th className="p-3 font-bold text-right">Prev → New</th>
                <th className="p-3 font-bold text-right">Sold</th>
                <th className="p-3 font-bold text-right">Rate</th>
                <th className="p-3 font-bold text-right">Value</th>
              </tr>
            </thead>
            <tbody>
              {data.audits.map((a) => (
                <tr key={a.id} data-testid={`audit-row-${a.id}`} className="border-b last:border-0 hover:bg-secondary/30">
                  <td className="p-3 whitespace-nowrap text-muted-foreground">{new Date(a.at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="p-3 font-semibold max-w-[220px] truncate">{a.product_title}{a.sold_out && <Badge className="ml-2 bg-blue-100 text-blue-800 border-blue-300 text-[10px]">Sold Out</Badge>}</td>
                  <td className="p-3"><div className="font-semibold">{a.company_name}</div><div className="text-xs text-muted-foreground">{a.mobile}</div></td>
                  <td className="p-3 text-right whitespace-nowrap">{a.prev_qty} → {a.new_qty}</td>
                  <td className="p-3 text-right font-bold text-amber-700">{a.qty_sold}</td>
                  <td className="p-3 text-right whitespace-nowrap">{rupee(a.rate)}/{(a.rate_unit || "").toLowerCase()}</td>
                  <td className="p-3 text-right font-extrabold text-primary">{rupee(a.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
